import * as React from 'react';
import { useEffect, useRef, useCallback } from 'react';
import { cn } from '@/lib/utils';

/* -------------------------------------------------------------------------- */
/*  Context                                                                    */
/* -------------------------------------------------------------------------- */

interface SelectContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  value: string;
  onValueChange: (value: string) => void;
  triggerRef: React.RefObject<HTMLButtonElement>;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
}

const SelectContext = React.createContext<SelectContextValue | undefined>(undefined);

function useSelect() {
  const ctx = React.useContext(SelectContext);
  if (!ctx) throw new Error('Select compound components must be used within <Select>');
  return ctx;
}

/* -------------------------------------------------------------------------- */
/*  Select (root)                                                              */
/* -------------------------------------------------------------------------- */

interface SelectProps {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  children: React.ReactNode;
}

function Select({
  value: controlledValue,
  defaultValue = '',
  onValueChange,
  children,
}: SelectProps) {
  const [uncontrolledValue, setUncontrolledValue] = React.useState(defaultValue);
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const triggerRef = useRef<HTMLButtonElement>(null!);

  const currentValue = controlledValue !== undefined ? controlledValue : uncontrolledValue;
  const handleValueChange = (val: string) => {
    if (controlledValue === undefined) setUncontrolledValue(val);
    setSearchQuery('');
    onValueChange?.(val);
  };

  return (
    <SelectContext.Provider
      value={{
        open,
        setOpen,
        value: currentValue,
        onValueChange: handleValueChange,
        triggerRef,
        searchQuery,
        setSearchQuery,
      }}
    >
      <div className="relative">{children}</div>
    </SelectContext.Provider>
  );
}

/* -------------------------------------------------------------------------- */
/*  SelectGroup                                                                */
/* -------------------------------------------------------------------------- */

function SelectGroup({ children, className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('p-1', className)} role="group" {...props}>
      {children}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  SelectValue                                                                */
/* -------------------------------------------------------------------------- */

interface SelectValueProps {
  placeholder?: string;
  children?: React.ReactNode;
}

function SelectValue({ placeholder, children }: SelectValueProps) {
  const { value } = useSelect();
  if (!value)
    return (
      <span className="block min-w-0 truncate text-muted-foreground">{placeholder || ''}</span>
    );
  if (children) return <span className="block min-w-0 truncate">{children}</span>;
  return <span className="block min-w-0 truncate">{value}</span>;
}

/* -------------------------------------------------------------------------- */
/*  SelectTrigger                                                              */
/* -------------------------------------------------------------------------- */

interface SelectTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {}

const SelectTrigger = React.forwardRef<HTMLButtonElement, SelectTriggerProps>(
  ({ className, children, ...props }, ref) => {
    const { open, setOpen, triggerRef } = useSelect();

    const combinedRef = (node: HTMLButtonElement | null) => {
      if (typeof ref === 'function') ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLButtonElement | null>).current = node;
      (triggerRef as React.MutableRefObject<HTMLButtonElement | null>).current = node;
    };

    return (
      <button
        ref={combinedRef}
        type="button"
        role="combobox"
        aria-expanded={open}
        className={cn(
          'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
          className
        )}
        onClick={() => setOpen(!open)}
        {...props}
      >
        <span className="min-w-0 flex-1 text-left">{children}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="ml-2 h-4 w-4 opacity-50"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
    );
  }
);
SelectTrigger.displayName = 'SelectTrigger';

/* -------------------------------------------------------------------------- */
/*  SelectContent                                                              */
/* -------------------------------------------------------------------------- */

interface SelectContentProps extends React.HTMLAttributes<HTMLDivElement> {
  position?: 'popper' | 'item-aligned';
  searchable?: boolean;
  searchPlaceholder?: string;
}

const SelectContent = React.forwardRef<HTMLDivElement, SelectContentProps>(
  (
    {
      className,
      children,
      position = 'popper',
      searchable = false,
      searchPlaceholder = '搜索...',
      ...props
    },
    ref
  ) => {
    const { open, setOpen, triggerRef, searchQuery, setSearchQuery } = useSelect();
    const contentRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    const handleClickOutside = useCallback(
      (e: MouseEvent) => {
        if (
          contentRef.current &&
          !contentRef.current.contains(e.target as Node) &&
          triggerRef.current &&
          !triggerRef.current.contains(e.target as Node)
        ) {
          setOpen(false);
        }
      },
      [setOpen, triggerRef]
    );

    useEffect(() => {
      if (open) {
        document.addEventListener('mousedown', handleClickOutside);
        if (searchable && searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [open, handleClickOutside, searchable]);

    if (!open) return null;

    return (
      <div
        ref={(node) => {
          (contentRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }}
        className={cn(
          'absolute z-50 max-h-96 min-w-[8rem] overflow-auto rounded-md border bg-popover text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95',
          position === 'popper' && 'mt-1 w-full',
          className
        )}
        {...props}
      >
        {searchable && (
          <div className="px-2 pt-2 pb-1">
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
        )}
        <div className="p-1">{children}</div>
      </div>
    );
  }
);
SelectContent.displayName = 'SelectContent';

/* -------------------------------------------------------------------------- */
/*  SelectLabel                                                                */
/* -------------------------------------------------------------------------- */

const SelectLabel = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('py-1.5 pl-8 pr-2 text-sm font-semibold', className)} {...props} />
  )
);
SelectLabel.displayName = 'SelectLabel';

/* -------------------------------------------------------------------------- */
/*  SelectItem                                                                 */
/* -------------------------------------------------------------------------- */

interface SelectItemProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string;
  disabled?: boolean;
  searchKeywords?: string;
}

const SelectItem = React.forwardRef<HTMLDivElement, SelectItemProps>(
  ({ className, children, value: itemValue, disabled, searchKeywords, ...props }, ref) => {
    const { value, onValueChange, setOpen, searchQuery } = useSelect();
    const isSelected = value === itemValue;

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const searchableText = (searchKeywords ?? itemValue).toLowerCase();
      if (!searchableText.includes(q)) {
        return null;
      }
    }

    return (
      <div
        ref={ref}
        role="option"
        aria-selected={isSelected}
        aria-disabled={disabled}
        className={cn(
          'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground',
          disabled && 'pointer-events-none opacity-50',
          className
        )}
        onClick={() => {
          if (!disabled) {
            onValueChange(itemValue);
            setOpen(false);
          }
        }}
        {...props}
      >
        {isSelected && (
          <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </span>
        )}
        <span className="min-w-0 flex-1 overflow-hidden">{children}</span>
      </div>
    );
  }
);
SelectItem.displayName = 'SelectItem';

/* -------------------------------------------------------------------------- */
/*  SelectSeparator                                                            */
/* -------------------------------------------------------------------------- */

const SelectSeparator = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('-mx-1 my-1 h-px bg-muted', className)} {...props} />
  )
);
SelectSeparator.displayName = 'SelectSeparator';

/* -------------------------------------------------------------------------- */
/*  Exports                                                                    */
/* -------------------------------------------------------------------------- */

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
};
